// Canvas history: Back / Forward through what this chat has opened, and the "In this chat" list (DES1
// CANVAS-HISTORY.md). Namespace `canvas-nav`; strings: DES2 canvas-history-24.json. {name} is never inflected.
// The kind words (App, Deck, Report, Document, Spreadsheet, Chart) are the Library's own (artifact-panels); File is new.
// canvasWidth names the line beside a docked canvas (canvas-divider.tsx) for assistive technology: the canvas is the
// pane it resizes; the word for canvas is the one meeting-member-notes uses.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  back: 'Back to {name}',
  forward: 'Forward to {name}',
  inThisChat: 'In this chat',
  switcherLabel: 'Switch item, current: {name}',
  current: 'Open now',
  unavailable: 'No longer available',
  kindFile: 'File',
  canvasWidth: 'Canvas width',
};

export type CanvasNavCopyKey = keyof typeof en;
export const canvasNavCopy: NamespaceCatalog<CanvasNavCopyKey> = {
  en,
  sv: {
    back: 'Tillbaka till {name}',
    forward: 'Framåt till {name}',
    inThisChat: 'I den här chatten',
    switcherLabel: 'Byt objekt, nu: {name}',
    current: 'Öppen nu',
    unavailable: 'Inte längre tillgänglig',
    kindFile: 'Fil',
    canvasWidth: 'Arbetsytans bredd',
  },
  da: {
    back: 'Tilbage til {name}',
    forward: 'Frem til {name}',
    inThisChat: 'I denne chat',
    switcherLabel: 'Skift element, nu: {name}',
    current: 'Åben nu',
    unavailable: 'Ikke længere tilgængelig',
    kindFile: 'Fil',
    canvasWidth: 'Arbejdsfladens bredde',
  },
  de: {
    back: 'Zurück zu {name}',
    forward: 'Weiter zu {name}',
    inThisChat: 'In diesem Chat',
    switcherLabel: 'Element wechseln, aktuell: {name}',
    current: 'Jetzt geöffnet',
    unavailable: 'Nicht mehr verfügbar',
    kindFile: 'Datei',
    canvasWidth: 'Breite der Arbeitsfläche',
  },
  fr: {
    back: 'Retour à {name}',
    forward: 'Avancer vers {name}',
    inThisChat: 'Dans ce chat',
    switcherLabel: 'Changer d’élément, actuel : {name}',
    current: 'Ouvert maintenant',
    unavailable: 'Plus disponible',
    kindFile: 'Fichier',
    canvasWidth: 'Largeur de l’espace de travail',
  },
  es: {
    back: 'Volver a {name}',
    forward: 'Avanzar a {name}',
    inThisChat: 'En este chat',
    switcherLabel: 'Cambiar de elemento, actual: {name}',
    current: 'Abierto ahora',
    unavailable: 'Ya no está disponible',
    kindFile: 'Archivo',
    canvasWidth: 'Ancho del espacio de trabajo',
  },
  it: {
    back: 'Torna a {name}',
    forward: 'Vai avanti a {name}',
    inThisChat: 'In questa chat',
    switcherLabel: 'Cambia elemento, attuale: {name}',
    current: 'Aperto ora',
    unavailable: 'Non più disponibile',
    kindFile: 'File',
    canvasWidth: 'Larghezza dell’area di lavoro',
  },
  pt: {
    back: 'Voltar a {name}',
    forward: 'Avançar para {name}',
    inThisChat: 'Neste chat',
    switcherLabel: 'Mudar de item, atual: {name}',
    current: 'Aberto agora',
    unavailable: 'Já não está disponível',
    kindFile: 'Ficheiro',
    canvasWidth: 'Largura da área de trabalho',
  },
  nl: {
    back: 'Terug naar {name}',
    forward: 'Vooruit naar {name}',
    inThisChat: 'In deze chat',
    switcherLabel: 'Item wisselen, huidig: {name}',
    current: 'Nu geopend',
    unavailable: 'Niet meer beschikbaar',
    kindFile: 'Bestand',
    canvasWidth: 'Breedte van de werkruimte',
  },
  fi: {
    back: 'Takaisin: {name}',
    forward: 'Eteenpäin: {name}',
    inThisChat: 'Tässä chatissa',
    switcherLabel: 'Vaihda kohdetta, nyt: {name}',
    current: 'Auki nyt',
    unavailable: 'Ei enää käytettävissä',
    kindFile: 'Tiedosto',
    canvasWidth: 'Työtilan leveys',
  },
  et: {
    back: 'Tagasi: {name}',
    forward: 'Edasi: {name}',
    inThisChat: 'Selles vestluses',
    switcherLabel: 'Vaheta üksust, praegu: {name}',
    current: 'Praegu avatud',
    unavailable: 'Pole enam saadaval',
    kindFile: 'Fail',
    canvasWidth: 'Tööala laius',
  },
  bg: {
    back: 'Назад към {name}',
    forward: 'Напред към {name}',
    inThisChat: 'В този чат',
    switcherLabel: 'Смяна на елемент, текущ: {name}',
    current: 'Отворено сега',
    unavailable: 'Вече не е налично',
    kindFile: 'Файл',
    canvasWidth: 'Ширина на работното платно',
  },
  cs: {
    back: 'Zpět na: {name}',
    forward: 'Vpřed na: {name}',
    inThisChat: 'V tomto chatu',
    switcherLabel: 'Přepnout položku, aktuální: {name}',
    current: 'Právě otevřeno',
    unavailable: 'Již není k dispozici',
    kindFile: 'Soubor',
    canvasWidth: 'Šířka pracovní plochy',
  },
  sk: {
    back: 'Späť na: {name}',
    forward: 'Dopredu na: {name}',
    inThisChat: 'V tomto chate',
    switcherLabel: 'Prepnúť položku, aktuálna: {name}',
    current: 'Práve otvorené',
    unavailable: 'Už nie je k dispozícii',
    kindFile: 'Súbor',
    canvasWidth: 'Šírka pracovnej plochy',
  },
  pl: {
    back: 'Wstecz do: {name}',
    forward: 'Dalej do: {name}',
    inThisChat: 'W tym czacie',
    switcherLabel: 'Przełącz element, bieżący: {name}',
    current: 'Teraz otwarte',
    unavailable: 'Już niedostępne',
    kindFile: 'Plik',
    canvasWidth: 'Szerokość obszaru roboczego',
  },
  hr: {
    back: 'Natrag na: {name}',
    forward: 'Naprijed na: {name}',
    inThisChat: 'U ovom chatu',
    switcherLabel: 'Promijeni stavku, trenutačno: {name}',
    current: 'Sada otvoreno',
    unavailable: 'Više nije dostupno',
    kindFile: 'Datoteka',
    canvasWidth: 'Širina radne površine',
  },
  sl: {
    back: 'Nazaj na: {name}',
    forward: 'Naprej na: {name}',
    inThisChat: 'V tem klepetu',
    switcherLabel: 'Zamenjaj element, trenutno: {name}',
    current: 'Zdaj odprto',
    unavailable: 'Ni več na voljo',
    kindFile: 'Datoteka',
    canvasWidth: 'Širina delovne površine',
  },
  hu: {
    back: 'Vissza ide: {name}',
    forward: 'Előre ide: {name}',
    inThisChat: 'Ebben a csevegésben',
    switcherLabel: 'Elem váltása, jelenlegi: {name}',
    current: 'Most megnyitva',
    unavailable: 'Már nem érhető el',
    kindFile: 'Fájl',
    canvasWidth: 'Munkafelület szélessége',
  },
  el: {
    back: 'Πίσω σε: {name}',
    forward: 'Μπροστά σε: {name}',
    inThisChat: 'Σε αυτή τη συνομιλία',
    switcherLabel: 'Αλλαγή στοιχείου, τρέχον: {name}',
    current: 'Ανοιχτό τώρα',
    unavailable: 'Δεν είναι πλέον διαθέσιμο',
    kindFile: 'Αρχείο',
    canvasWidth: 'Πλάτος χώρου εργασίας',
  },
  ro: {
    back: 'Înapoi la {name}',
    forward: 'Înainte la {name}',
    inThisChat: 'În acest chat',
    switcherLabel: 'Schimbă elementul, curent: {name}',
    current: 'Deschis acum',
    unavailable: 'Nu mai este disponibil',
    kindFile: 'Fișier',
    canvasWidth: 'Lățimea spațiului de lucru',
  },
  lt: {
    back: 'Atgal: {name}',
    forward: 'Pirmyn: {name}',
    inThisChat: 'Šiame pokalbyje',
    switcherLabel: 'Perjungti elementą, dabartinis: {name}',
    current: 'Dabar atidaryta',
    unavailable: 'Nebepasiekiama',
    kindFile: 'Failas',
    canvasWidth: 'Darbo srities plotis',
  },
  lv: {
    back: 'Atpakaļ: {name}',
    forward: 'Uz priekšu: {name}',
    inThisChat: 'Šajā tērzēšanā',
    switcherLabel: 'Pārslēgt vienumu, pašreizējais: {name}',
    current: 'Tagad atvērts',
    unavailable: 'Vairs nav pieejams',
    kindFile: 'Fails',
    canvasWidth: 'Darba laukuma platums',
  },
  ga: {
    back: 'Siar go: {name}',
    forward: 'Ar aghaidh go: {name}',
    inThisChat: 'Sa chomhrá seo',
    switcherLabel: 'Athraigh mír, reatha: {name}',
    current: 'Oscailte anois',
    unavailable: 'Níl ar fáil a thuilleadh',
    kindFile: 'Comhad',
    canvasWidth: 'Leithead an spáis oibre',
  },
  mt: {
    back: 'Lura għal: {name}',
    forward: '’Il quddiem għal: {name}',
    inThisChat: 'F’din iċ-chat',
    switcherLabel: 'Biddel l-oġġett, attwali: {name}',
    current: 'Miftuħ issa',
    unavailable: 'M’għadux disponibbli',
    kindFile: 'Fajl',
    canvasWidth: 'Wisa’ tal-ispazju tax-xogħol',
  },
};
export default canvasNavCopy;
